import React, { useState, useRef } from "react";
import usePDA1081_R01Store from "./PDA1081_R01Store";
import { usePDA1081_R01Content } from "./PDA1081_R01Content";

const PDA1081_R01Form = () => {
  const {
    scanValue,
    setScanValue,
    scannedItem,
    returnQty,
    setReturnQty,
    loading,
    returnItems,
  } = usePDA1081_R01Store();

  const scanInputRef = useRef<HTMLInputElement>(null);
  const { handleScanItem, handleAddToGrid, handleSaveAll, handleCancel } =
    usePDA1081_R01Content(scanInputRef);

  const [keyboardEnabled, setKeyboardEnabled] = useState(false);
  const [lastTapTime, setLastTapTime] = useState(0);

  const handleDoubleTap = () => {
    const now = Date.now();
    if (now - lastTapTime < 300) {
      setKeyboardEnabled(true);
      scanInputRef.current?.focus();
    }
    setLastTapTime(now);
  };

  const toggleKeyboard = () => {
    setKeyboardEnabled(!keyboardEnabled);
    if (!keyboardEnabled) setTimeout(() => scanInputRef.current?.focus(), 100);
  };

  const handleScanKeyDown = async (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const ok = await handleScanItem();
      if (ok) setTimeout(() => document.getElementById("returnQty")?.focus(), 80);
    }
  };

  const handleQtyKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddToGrid();
    }
  };

  return (
    <div className="w-full bg-white p-4 border-b border-gray-200">
      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <label className="text-lg font-medium text-black w-20 flex-shrink-0">Scan</label>
          <div className="flex-1 flex gap-1">
            <input
              ref={scanInputRef}
              type="text"
              value={scanValue}
              onChange={(e) => setScanValue(e.target.value)}
              onKeyDown={handleScanKeyDown}
              onClick={handleDoubleTap}
              inputMode={keyboardEnabled ? "text" : "none"}
              disabled={loading}
              placeholder="품목/바코드 스캔"
              autoComplete="off"
              className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={toggleKeyboard}
              className={`w-10 flex-shrink-0 px-2 border border-gray-300 rounded transition-colors ${
                keyboardEnabled ? "bg-blue-500 text-white" : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
              title={keyboardEnabled ? "키보드 숨기기" : "키보드 표시"}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <label className="text-lg font-medium text-black w-20 flex-shrink-0">품목</label>
          <div className="flex-1 flex gap-1">
            <input
              type="text"
              value={scannedItem?.itemNm || ""}
              readOnly
              className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
            />
            <div className="w-10 flex-shrink-0"></div>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <label className="text-lg font-medium text-black w-20 flex-shrink-0">규격</label>
          <div className="flex-1 flex gap-1">
            <input
              type="text"
              value={scannedItem?.itemSpec || ""}
              readOnly
              className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
            />
            <div className="w-10 flex-shrink-0"></div>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <label className="text-lg font-medium text-black w-20 flex-shrink-0">반입가능</label>
          <div className="flex-1 flex gap-1">
            <input
              type="text"
              value={
                scannedItem?.availableQty != null
                  ? `${Number(scannedItem.availableQty).toLocaleString()} ${scannedItem.itemUnit ?? ""}`
                  : ""
              }
              readOnly
              className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
            />
            <div className="w-10 flex-shrink-0"></div>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <label className="text-lg font-medium text-black w-20 flex-shrink-0">반입량</label>
          <div className="flex-1 flex gap-1">
            <input
              id="returnQty"
              type="number"
              value={returnQty || ""}
              onChange={(e) => setReturnQty(Number(e.target.value))}
              onKeyDown={handleQtyKeyDown}
              disabled={loading || !scannedItem}
              placeholder="수량"
              min="0"
              step="1"
              className="w-20 px-2 text-lg border border-gray-300 rounded bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            />
            <button
              onClick={handleAddToGrid}
              disabled={loading || !scannedItem || !returnQty || returnQty <= 0}
              className="flex-1 bg-blue-500 text-white px-2 rounded text-lg font-medium hover:bg-blue-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              검수 {returnItems.length > 0 && `(${returnItems.length})`}
            </button>
          </div>
        </div>

        <div className="flex gap-1 pt-2">
          <button
            onClick={handleCancel}
            className="flex-1 py-1 text-lg bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            취소
          </button>
          <button
            onClick={handleSaveAll}
            disabled={loading || returnItems.length === 0}
            className="flex-1 py-1 text-lg bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {returnItems.length > 0 ? `반입(${returnItems.length})` : "반입"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PDA1081_R01Form;
